import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  type MRT_ColumnFiltersState,
  type MRT_PaginationState,
  type MRT_SortingState,
} from 'mantine-react-table';
import { account } from '@/api';
import DataTable from './DataTable';
import { useApiErrorHandler } from '@/account/hook/useApiErrorHandler';
import { useGlobalApi } from '@/account/hook/useGlobalApi';
import BaseLoadingSpinner from '@/base/Loading/Spinner';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { useLocalStorage } from '@uidotdev/usehooks';

interface Params {
  filters: any
  sorting: any
  pagination: any
  search?: string
}

const loadTenants = ({ filters, sorting, pagination }: Params) => {
  const { createGlobalApiAclRequest } = useGlobalApi();
  return useQuery<account.SubscriptionSearchResponse, Error>({
    queryKey: [account.subscriptionSearchQueryKey, pagination, filters, sorting],
    queryFn: async (): Promise<account.SubscriptionSearchResponse> => {
      const searchRequest: account.SubscriptionSearchRequest = {
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize ?? 10,
      };
      if (sorting && sorting.length > 0) {
        searchRequest.orderByField = sorting['0'].id;
        searchRequest.isOrderedAsc = !sorting['0'].desc;
      }
      if (filters && filters.length > 0) {
        for (const col of filters) {
          if (col.id === 'state') {
            searchRequest.state = parseInt(col.value, 10);
          }
          if (col.id === 'tenant') {
            searchRequest.tenantName = col.value;
          }
          if (col.id === 'tenantId') {
            searchRequest.tenantId = col.value;
          }
        }
      }
      return await account.searchSubscriptions(createGlobalApiAclRequest())(searchRequest);
    },
    // enabled: !isTenantLoading,
    keepPreviousData: true,
    staleTime: 30_000
  });
};

const LOCAL_TABLE_STORAGE_KEY = 'aaa_bill_table';

const BillTable = () => {
  const { tenantCode } = useRegisterRoute();
  const [filters, setFilters] = useLocalStorage<MRT_ColumnFiltersState>(`${LOCAL_TABLE_STORAGE_KEY}_filter_${tenantCode}`, []);
  const [sorting, setSorting] = useLocalStorage<MRT_SortingState>(`${LOCAL_TABLE_STORAGE_KEY}_sorting_${tenantCode}`, []);
  const [pagination, setPagination] = useLocalStorage<MRT_PaginationState>(`${LOCAL_TABLE_STORAGE_KEY}_pagination_${tenantCode}`, {
    pageIndex: 0,
    pageSize: 10,
  });
  const { reportToGlobal } = useApiErrorHandler();

  const { data, isLoading, error, isError, isFetching, refetch } = loadTenants({ filters, sorting, pagination });
  useEffect(
    () => {
      if (error) {
        reportToGlobal(error);
      }
    },
    [error]
  );

  if (!data || isLoading) {
    return (
      <BaseLoadingSpinner />
    );
  }

  return (
    <DataTable
      data={(data.subscriptionList && Array.isArray(data.subscriptionList)) ? data.subscriptionList : []}
      isLoading={isLoading}
      isError={isError}
      isFetching={isFetching}
      refetch={refetch}
      filters={filters}
      setFilters={setFilters}
      sorting={sorting}
      setSorting={setSorting}
      // search={search}
      // setSearch={setSearch}
      pagination={pagination}
      setPagination={setPagination}
      rowCount={data.total ?? 0}
    />
  );
};

export default BillTable;
